/**
 * Column widths of the library tables (AP-128): dragging, the arrow keys on
 * a grip, back to the default, and keeping them per table and user in the
 * browser. Pure functions; the table in LibraryTable.tsx only wires them up.
 */

export interface WidthColumn {
  key: string;
  defaultWidth: number;
  minWidth?: number;
}

export type TableWidths = Record<string, number>;

/** Narrower than this a column shows nothing useful. */
export const MIN_COLUMN_WIDTH = 40;
/** Pixels per arrow key on a grip, and with Shift held. */
export const WIDTH_STEP = 8;
export const WIDTH_STEP_LARGE = 32;

export function clampWidth(width: number, min = MIN_COLUMN_WIDTH): number {
  return Math.max(min, Math.round(width));
}

/**
 * Width after dragging an edge from `origin` to `current`: the pointer's
 * total travel since the press, from the width at the press (the lesson of
 * the schedule's TaskList), never below the minimum.
 */
export function resizedWidth(base: number, origin: number, current: number, min = MIN_COLUMN_WIDTH): number {
  return clampWidth(base + (current - origin), min);
}

/** Width after a key on a grip; null for keys that do not change it. */
export function stepWidth(width: number, key: string, shift: boolean, min = MIN_COLUMN_WIDTH): number | null {
  const step = shift ? WIDTH_STEP_LARGE : WIDTH_STEP;
  if (key === 'ArrowRight') return clampWidth(width + step, min);
  if (key === 'ArrowLeft') return clampWidth(width - step, min);
  return null;
}

export function defaultWidths(columns: readonly WidthColumn[]): TableWidths {
  return Object.fromEntries(columns.map((c) => [c.key, c.defaultWidth]));
}

/** One entry per table and user: two people sharing a browser keep their own. */
export function tableWidthsKey(tableId: string, userId: string | null | undefined): string {
  return `pst.table.${tableId}.${userId ?? 'anonym'}`;
}

type WidthStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The browser's storage, or none - reaching it can throw (privacy settings, sandboxed frames). */
function browserStorage(): WidthStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Saved widths over the defaults; whatever is missing, too narrow or unreadable keeps its default. */
export function loadTableWidths(
  tableId: string,
  userId: string | null | undefined,
  columns: readonly WidthColumn[],
  storage: WidthStorage | null = browserStorage(),
): TableWidths {
  const widths = defaultWidths(columns);
  try {
    const raw = storage?.getItem(tableWidthsKey(tableId, userId));
    if (!raw) return widths;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const c of columns) {
      const value = parsed?.[c.key];
      if (typeof value === 'number' && Number.isFinite(value) && value >= (c.minWidth ?? MIN_COLUMN_WIDTH)) widths[c.key] = Math.round(value);
    }
    return widths;
  } catch {
    // A browser with storage disabled or a broken entry is not a reason to
    // fail; the defaults are perfectly usable.
    return defaultWidths(columns);
  }
}

export function saveTableWidths(
  tableId: string,
  userId: string | null | undefined,
  widths: TableWidths,
  storage: WidthStorage | null = browserStorage(),
): void {
  try {
    storage?.setItem(tableWidthsKey(tableId, userId), JSON.stringify(widths));
  } catch {
    // Ignored for the same reason: the widths then last until the page is left.
  }
}
